import { Vue, Component, Prop, Emit } from "vue-property-decorator";
import * as tsx from "vue-tsx-support";
import moreIcon from "@/assets/img/icon-more.svg";
import { FormInputWithDropdown } from "../forms/inputs/FormInputWithDropdown.component";
import {
  getTypedBFormSelect,
  getTypedBFormSelectOption,
  IBFormSelectEvents,
} from "@/lib/typed-bootstrap";
import {
  GraphQLOperationType,
  hasCreateMutationType,
  hasDeleteMutationType,
  hasListByParentQueryType,
  hasListQueryType,
  hasObjectQueryType,
  hasUpdateMutationType,
} from "@/models";
import {
  FormNestedCheckbox,
  IFormNestedCheckboxState,
} from "../forms/inputs/FormNestedCheckbox.component";
import { isNotNullable } from "@/utils";
import {
  MutationTypeEnum,
  PipelineStepEnum,
  QueryTypeEnum,
} from "@graphapi-io/declarations";

const BFormSelect = getTypedBFormSelect<PipelineStepEnum>();
const BFormSelectOption = getTypedBFormSelectOption<PipelineStepEnum | null>();

export type ISelectableOperationType = GraphQLOperationType | "All";

const moreIconSrc = moreIcon;

@Component
export class ObjectTypeTriggerStepSelect extends Vue {
  _tsx!: tsx.DeclareProps<{
    stepValue: ObjectTypeTriggerStepSelect["stepValue"];
    operationTypes: ObjectTypeTriggerStepSelect["operationTypes"];
    id: ObjectTypeTriggerStepSelect["id"];
  }> &
    tsx.DeclareOnEvents<
      IBFormSelectEvents<PipelineStepEnum> & {
        onConnectorChange: string;
        onMutationTypesChange: ISelectableOperationType;
      }
    >;

  @Prop({ required: true }) stepValue: PipelineStepEnum | null;
  @Prop({ required: true }) operationTypes: (
    | MutationTypeEnum
    | QueryTypeEnum
  )[];
  @Prop({ required: true }) id: string;

  @Emit("mutationTypesChange") onMutationTypesChange(
    _value: ISelectableOperationType,
  ) {}

  private get hasCreate() {
    return hasCreateMutationType(this.operationTypes);
  }

  private get hasUpdate() {
    return hasUpdateMutationType(this.operationTypes);
  }

  private get hasDelete() {
    return hasDeleteMutationType(this.operationTypes);
  }

  private get hasList() {
    return hasListQueryType(this.operationTypes);
  }

  private get hasListByParent() {
    return hasListByParentQueryType(this.operationTypes);
  }

  private get hasObject() {
    return hasObjectQueryType(this.operationTypes);
  }

  private get hasAll() {
    return (
      this.hasCreate &&
      this.hasUpdate &&
      this.hasDelete &&
      this.hasList &&
      this.hasListByParent &&
      this.hasObject
    );
  }

  private get stepOptions() {
    const postfix = this.hasAll
      ? this.$t("model_widget_triggers_all")
      : [
          this.hasCreate ? this.$t("model_widget_triggers_create") : null,
          this.hasUpdate ? this.$t("model_widget_triggers_update") : null,
          this.hasDelete ? this.$t("model_widget_triggers_delete") : null,
          this.hasList ? this.$t("model_widget_triggers_on_list") : null,
          this.hasListByParent
            ? this.$t("model_widget_triggers_on_list_by_parent")
            : null,
          this.hasObject ? this.$t("model_widget_triggers_on_object") : null,
        ]
          .filter(isNotNullable)
          .join(", ");

    return [
      {
        value: PipelineStepEnum.BEFORE,
        text: `${this.$t("model_widget_triggers_before")} ${postfix}`,
      },
      {
        value: PipelineStepEnum.AFTER,
        text: `${this.$t("model_widget_triggers_after")} ${postfix}`,
      },
    ];
  }

  private get operationTypeChanges() {
    return {
      name: this.$t("model_widget_triggers_on_all").toString(),
      selected: this.hasAll,
      children: [
        {
          name: this.$t("model_widget_triggers_on_create").toString(),
          selected: this.hasCreate,
          value: MutationTypeEnum.CREATE,
          disabled:
            this.hasCreate &&
            !this.hasDelete &&
            !this.hasUpdate &&
            !this.hasList &&
            !this.hasListByParent &&
            !this.hasObject,
        },
        {
          name: this.$t("model_widget_triggers_on_update").toString(),
          selected: this.hasUpdate,
          value: MutationTypeEnum.UPDATE,
          disabled:
            this.hasUpdate &&
            !this.hasCreate &&
            !this.hasDelete &&
            !this.hasList &&
            !this.hasListByParent &&
            !this.hasObject,
        },
        {
          name: this.$t("model_widget_triggers_on_delete").toString(),
          selected: this.hasDelete,
          value: MutationTypeEnum.DELETE,
          disabled:
            this.hasDelete &&
            !this.hasUpdate &&
            !this.hasCreate &&
            !this.hasList &&
            !this.hasListByParent &&
            !this.hasObject,
        },
        {
          name: this.$t("model_widget_triggers_on_list").toString(),
          selected: this.hasList,
          value: QueryTypeEnum.LIST,
          disabled:
            this.hasList &&
            !this.hasUpdate &&
            !this.hasCreate &&
            !this.hasDelete &&
            !this.hasListByParent &&
            !this.hasObject,
        },
        {
          name: this.$t("model_widget_triggers_on_list_by_parent").toString(),
          selected: this.hasListByParent,
          value: QueryTypeEnum.LIST_BY_PARENT,
          disabled:
            this.hasListByParent &&
            !this.hasUpdate &&
            !this.hasCreate &&
            !this.hasDelete &&
            !this.hasList &&
            !this.hasObject,
        },
        {
          name: this.$t("model_widget_triggers_on_object").toString(),
          selected: this.hasObject,
          value: QueryTypeEnum.OBJECT,
          disabled:
            this.hasObject &&
            !this.hasUpdate &&
            !this.hasCreate &&
            !this.hasDelete &&
            !this.hasList &&
            !this.hasListByParent,
        },
      ],
    };
  }

  private handleMutationTypesChange(value: IFormNestedCheckboxState) {
    if (value.selected !== this.operationTypeChanges.selected) {
      this.onMutationTypesChange("All");
      return;
    }

    if (!this.operationTypeChanges.children || !value.children) return;

    for (let i = 0; i < this.operationTypeChanges.children.length; i++) {
      if (
        this.operationTypeChanges.children[i].selected !==
        value.children[i].selected
      ) {
        this.onMutationTypesChange(this.operationTypeChanges.children[i].value);
        return;
      }
    }
  }

  render() {
    return (
      <FormInputWithDropdown
        id={this.id}
        iconSrc={moreIconSrc}
        titleContent={this.$t(
          "model_widget_triggers_mutation_title",
        ).toString()}
        disclaimerContent={this.$t(
          "model_widget_triggers_disclaimer",
        ).toString()}
        scopedSlots={{
          dropdownContent: () => (
            <FormNestedCheckbox
              id={`${this.id}-nestedCheckbox`}
              value={this.operationTypeChanges}
              onChange={this.handleMutationTypesChange}
              class="mt-2"
            />
          ),
          input: (inputClass) => (
            <BFormSelect
              class={[inputClass, "default__form-input", "form-select__input"]}
              value={this.stepValue ?? null}
              options={this.stepOptions}
              required
              on={this.$listeners}
            >
              <template slot="first">
                <BFormSelectOption value={null} disabled>
                  {this.$t("api_details_data_types_select_trigger_step")}
                </BFormSelectOption>
              </template>
            </BFormSelect>
          ),
        }}
      />
    );
  }
}
